import clinicAPI from "../services/clinicApi";
import { isIos, isStandaloneMode } from "./pwaUtils";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

/** Push доступен: на iOS только в установленном PWA (16.4+). */
export function isPushSupported() {
  if (typeof window === "undefined") return false;
  if (!("serviceWorker" in navigator) || !("Notification" in window)) return false;
  if (isIos() && !isStandaloneMode()) return false;
  return "PushManager" in window;
}

export function canPromptForPush() {
  if (!isPushSupported()) return false;
  return Notification.permission === "default";
}

export function pushNeedsPwaInstall() {
  return isIos() && !isStandaloneMode();
}

let registerPromise = null;

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  if (registerPromise) return registerPromise;

  registerPromise = (async () => {
    try {
      return await navigator.serviceWorker.register("/service-worker.js", {
        scope: "/",
        updateViaCache: "none",
      });
    } catch {
      registerPromise = null;
      return null;
    }
  })();

  return registerPromise;
}

export async function getPushPermission() {
  if (!("Notification" in window)) return "unsupported";
  if (pushNeedsPwaInstall()) return "needs_pwa";
  if (!isPushSupported()) return "unsupported";
  return Notification.permission;
}

/** Есть ли активная push-подписка (permission может остаться granted после отключения). */
export async function hasActivePushSubscription() {
  if (!isPushSupported()) return false;
  if (Notification.permission !== "granted") return false;
  try {
    const reg = await registerServiceWorker();
    if (!reg) return false;
    const ready = await navigator.serviceWorker.ready;
    const sub = await ready.pushManager.getSubscription();
    return Boolean(sub);
  } catch {
    return false;
  }
}

function isApplePushEndpoint(endpoint) {
  return String(endpoint || "").includes("web.push.apple.com");
}

function withTimeout(promise, ms, label = "timeout") {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error(label));
    }, ms);
    promise
      .then((value) => {
        window.clearTimeout(timer);
        resolve(value);
      })
      .catch((err) => {
        window.clearTimeout(timer);
        reject(err);
      });
  });
}

async function waitForServiceWorkerReady() {
  const waitMs = isIos() ? 8000 : 15000;
  const reg = await withTimeout(
    navigator.serviceWorker.ready,
    waitMs,
    "Service Worker не успел запуститься",
  );
  if (!isIos() || navigator.serviceWorker.controller) return reg;
  await withTimeout(
    new Promise((resolve) => {
      const timer = window.setTimeout(resolve, 2000);
      navigator.serviceWorker.addEventListener(
        "controllerchange",
        () => {
          window.clearTimeout(timer);
          resolve();
        },
        { once: true },
      );
    }),
    2500,
    "Service Worker controller",
  );
  return navigator.serviceWorker.ready;
}

async function ensurePushSubscription(reg, publicKey) {
  let sub = await reg.pushManager.getSubscription();

  if (isIos() && isStandaloneMode() && sub && !isApplePushEndpoint(sub.endpoint)) {
    try {
      await sub.unsubscribe();
    } catch {
      /* ignore */
    }
    sub = null;
  }

  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  return sub;
}

async function subscribeWithApi(getKey, subscribeApi) {
  if (pushNeedsPwaInstall()) {
    throw new Error("На iPhone добавьте приложение на главный экран, затем включите уведомления");
  }
  if (!isPushSupported()) {
    throw new Error("Push не поддерживается в этом браузере");
  }

  // SW заранее — после диалога iOS часто «висит», если ready ещё не готов
  await registerServiceWorker();

  let permission = Notification.permission;
  if (permission === "default") {
    permission = await withTimeout(
      Notification.requestPermission(),
      isIos() ? 60000 : 30000,
      "Диалог разрешения не ответил",
    );
  }
  if (permission !== "granted") {
    throw new Error(
      permission === "denied"
        ? "Уведомления запрещены в настройках iPhone"
        : "Разрешение на уведомления не получено",
    );
  }

  const run = async () => {
    const reg = await waitForServiceWorkerReady();

    const { publicKey } = await getKey();
    if (!publicKey) throw new Error("Push не настроен на сервере");

    const sub = await ensurePushSubscription(reg, publicKey);
    await subscribeApi(sub.toJSON());
    return sub;
  };

  return withTimeout(run(), isIos() ? 20000 : 30000, "Не удалось подключить уведомления");
}

export async function subscribeToPush() {
  return subscribeWithApi(
    () => clinicAPI.getPushPublicKey(),
    (sub) => clinicAPI.subscribePush(sub),
  );
}

export async function subscribeToAdminPush() {
  return subscribeWithApi(
    () => clinicAPI.getAdminPushPublicKey(),
    (sub) => clinicAPI.subscribeAdminPush(sub),
  );
}

export async function unsubscribeFromPush() {
  if (!("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  try {
    await clinicAPI.unsubscribePush(sub.endpoint);
  } catch {
    /* сервер мог уже не знать endpoint — локально всё равно отписываемся */
  }
  await sub.unsubscribe();
}

export async function unsubscribeFromAdminPush() {
  if (!("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  try {
    await clinicAPI.unsubscribeAdminPush(sub.endpoint);
  } catch {
    /* сервер мог уже не знать endpoint — локально всё равно отписываемся */
  }
  await sub.unsubscribe();
}

export async function sendTestPush() {
  return clinicAPI.sendTestPush();
}

export async function sendTestAdminPush() {
  return clinicAPI.sendTestAdminPush();
}

export async function syncPushSubscriptionIfGranted() {
  return syncPushWithApi(
    () => clinicAPI.getPushPublicKey(),
    (sub) => clinicAPI.subscribePush(sub),
  );
}

export async function syncAdminPushSubscriptionIfGranted() {
  return syncPushWithApi(
    () => clinicAPI.getAdminPushPublicKey(),
    (sub) => clinicAPI.subscribeAdminPush(sub),
  );
}

async function syncPushWithApi(getKey, subscribeApi) {
  if (!isPushSupported()) return false;
  if (Notification.permission !== "granted") return false;
  try {
    const run = async () => {
      const reg = await registerServiceWorker();
      if (!reg) return false;
      const ready = await waitForServiceWorkerReady();
      const { publicKey } = await getKey();
      if (!publicKey) return false;
      const sub = await ensurePushSubscription(ready, publicKey);
      await subscribeApi(sub.toJSON());
      return true;
    };
    return await withTimeout(run(), isIos() ? 12000 : 20000, "sync push timeout");
  } catch {
    return false;
  }
}
